import { resolveStagingLineVisibleDescription } from "@shared/dtoolsCatalogueMatch";
import { inferDtoolsEquipmentRole, inferDtoolsEquipmentWhyItMatters } from "@shared/dtoolsEquipmentRoles";
import type { DtoolsStagingLine } from "@shared/dtoolsJsonStaging";
import { parseCustomerExplanationStored } from "@shared/proposalItemCleoDescription";
import { formatProductBrandModelReference } from "@shared/productDisplayName";

function clean(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function firstSentence(text: string): string {
  const trimmed = clean(text);
  if (!trimmed) return "";
  const match = trimmed.match(/^(.+?[.!?])(?:\s|$)/);
  return (match?.[1] ?? trimmed).trim();
}

const CATALOGUE_INTRO_PATTERN = /^the\s+.+\s+is\s+(a|an)\s+/i;
const TECHNICAL_MODEL_PATTERN = /^[A-Z0-9][A-Z0-9._\-/]{1,}$/i;

/** Model codes like ITP-5500 or R2ic — poor primary customer identifiers. */
export function isTechnicalModelIdentifier(value: string): boolean {
  const trimmed = clean(value);
  if (!trimmed || trimmed.length > 32) return false;
  if (/\s{2,}/.test(trimmed)) return false;
  if (/^\d+["″]/.test(trimmed)) return false;
  if (/\b(speaker|screen|amplifier|touch|control|processor|projector)\b/i.test(trimmed)) return false;
  if (TECHNICAL_MODEL_PATTERN.test(trimmed)) return true;
  return /^[A-Z]{2,5}[-_]\d{2,}/i.test(trimmed);
}

export function isCatalogueLikeDescription(text: string): boolean {
  const value = clean(text);
  if (!value) return false;
  if (CATALOGUE_INTRO_PATTERN.test(value)) return true;
  if (/\b(high-quality|designed for|features include|specifications|rated at|offers a)\b/i.test(value)) {
    return true;
  }
  return false;
}

function inferPdfFriendlyDisplayName(line: DtoolsStagingLine): string | null {
  const haystack = [
    line.category,
    line.subcategory,
    line.polishedDescription,
    line.dtoolsDescription,
    line.cleoDescription,
    line.displayName,
  ]
    .map((value) => clean(value))
    .filter(Boolean)
    .join(" ");
  const brand = clean(line.manufacturer);

  if (/\b(touch\s*screen|touch\s*panel|touchpanel)\b/i.test(haystack)) {
    const size = haystack.match(/(\d+(?:\.\d+)?\s*(?:["″]|inch(?:es)?))/i)?.[1]?.replace(/\s+/g, "");
    const brandLabel = brand || "Touch";
    return size ? `${size} ${brandLabel} Touch Screen` : `${brandLabel} Touch Screen`;
  }

  if (/\b(in-?ceiling|ceiling speaker)\b/i.test(haystack)) {
    const size = haystack.match(/(\d+(?:\.\d+)?\s*(?:["″]|inch(?:es)?))/i)?.[1]?.replace(/\s+/g, "");
    return size ? `${size} In-Ceiling Speaker` : "In-Ceiling Speaker";
  }

  if (/\b(subwoofer|\bsub\b)\b/i.test(haystack)) {
    return brand ? `${brand} Subwoofer` : "Subwoofer";
  }

  const polished = clean(line.polishedDescription);
  const candidate = firstSentence(polished).replace(/\.$/, "");
  if (
    candidate &&
    candidate.length <= 56 &&
    !isTechnicalModelIdentifier(candidate) &&
    !isCatalogueLikeDescription(candidate)
  ) {
    return candidate;
  }

  return null;
}

/** Short customer-facing display name for PDF primary rows — display name leads, model is secondary. */
export function resolvePdfProductDisplayName(line: DtoolsStagingLine): string {
  const stored = clean(line.displayName);
  if (stored && !isTechnicalModelIdentifier(stored)) return stored;

  const inferred = inferPdfFriendlyDisplayName(line);
  if (inferred) return inferred;

  if (stored) return stored;

  const model = clean(line.model);
  if (model && !isTechnicalModelIdentifier(model)) return model;

  const brandModel = formatProductBrandModelReference({
    brand: line.manufacturer,
    model: line.model,
  });
  if (brandModel) return brandModel;

  const polished = clean(line.polishedDescription);
  if (polished && polished.length <= 80 && !isCatalogueLikeDescription(polished)) {
    return firstSentence(polished).replace(/\.$/, "");
  }

  return clean(line.dtoolsDescription) || "Product";
}

function generatePdfProductFallbackSummary(line: DtoolsStagingLine): string | null {
  const role = inferDtoolsEquipmentRole(line);
  const brand = clean(line.manufacturer);
  const display = resolvePdfProductDisplayName(line);

  const roleSummaries: Partial<Record<ReturnType<typeof inferDtoolsEquipmentRole>, string>> = {
    Projection: "Delivers a large-format cinema image for immersive movie nights at home.",
    "Video Processing": "Refines motion and colour so films and sports look more cinematic.",
    "Audio Processing": "Shapes surround sound and room correction for a more immersive listening experience.",
    Amplification: "Provides clean, controlled power so music and cinema playback stay effortless.",
    Subwoofers: "Adds weight and impact to movies, concerts, and everyday listening.",
    "Front Speakers": "Anchors dialogue and front-stage detail for music and cinema.",
    "Surround Speakers": "Extends sound around the room for a more enveloping experience.",
    "Height Speakers": "Adds overhead effects for a more three-dimensional sound field.",
    "Media Source": "Provides high-quality movie and music playback without streaming compromises.",
    Automation: "Brings everyday control of lighting, audio, and entertainment into one simple interface.",
    Networking: "Keeps entertainment and smart devices connected reliably throughout the home.",
    Display: "Provides a premium large-screen viewing experience for everyday entertainment.",
    Screen: "Creates a dedicated cinema canvas for projection in the viewing space.",
    Control: "Makes everyday control of the home simple for the whole family.",
    Integration: "Ensures the quoted equipment is installed, tested, and ready for daily use.",
  };

  const summary = roleSummaries[role];
  if (summary) return summary;
  if (display && display !== "Product") {
    return `${display} is included to complete the experience designed for this system.`;
  }
  if (brand) return `${brand} equipment selected for this scope.`;
  return null;
}

/**
 * PDF-only experience summary — prefers contribution copy over catalogue definitions.
 * Priority: customer why-you-need-it → customer what-this-is → visible description → fallback.
 */
export function resolvePdfProductExperienceSummary(line: DtoolsStagingLine, displayName: string): string | null {
  const hover = parseCustomerExplanationStored(line.customerExplanation);

  if (hover?.whyYouNeedIt) {
    const sentence = firstSentence(hover.whyYouNeedIt);
    if (sentence && !isCatalogueLikeDescription(sentence)) return sentence;
  }

  if (hover?.whatThisIs) {
    const sentence = firstSentence(hover.whatThisIs);
    if (sentence && !isCatalogueLikeDescription(sentence)) return sentence;
  }

  const visible = clean(resolveStagingLineVisibleDescription(line));
  if (visible && !isCatalogueLikeDescription(visible)) {
    const sentence = firstSentence(visible);
    if (sentence.toLowerCase() !== displayName.toLowerCase()) return sentence;
  }

  return generatePdfProductFallbackSummary(line);
}

/** One-sentence PDF-only “why it matters” block for primary components. */
export function resolvePdfProductWhyItMatters(line: DtoolsStagingLine): string | null {
  const hover = parseCustomerExplanationStored(line.customerExplanation);
  if (hover?.whyYouNeedIt) {
    const sentence = firstSentence(hover.whyYouNeedIt);
    if (sentence.length <= 140 && !isCatalogueLikeDescription(sentence)) return sentence;
  }
  return inferDtoolsEquipmentWhyItMatters(line);
}

/** @deprecated Use resolvePdfProductExperienceSummary — kept for table columns. */
export function resolvePdfProductDescription(line: DtoolsStagingLine, displayName: string): string | null {
  const summary = resolvePdfProductExperienceSummary(line, displayName);
  if (!summary) return null;
  const why = resolvePdfProductWhyItMatters(line);
  if (why && why.toLowerCase() === summary.toLowerCase()) return null;
  return summary;
}

export type PdfLineTableColumns = {
  item: string;
  description: string | null;
  quantity: string;
};

function formatPdfQuantity(line: DtoolsStagingLine): string {
  return String(line.quantity);
}

function resolvePdfServiceBenefit(line: DtoolsStagingLine, serviceTitle: string): string | null {
  const haystack = [
    line.polishedDescription,
    line.dtoolsDescription,
    line.cleoDescription,
    line.displayName,
  ]
    .map((value) => clean(value))
    .filter(Boolean)
    .join(" ");

  if (/\b(commission|handover|calibrat)\b/i.test(haystack)) {
    return "Calibration, performance verification, and client handover so the system is ready for daily use.";
  }
  if (/\b(install|installation|mount|rack build)\b/i.test(haystack)) {
    return "Professional installation, cable management, and on-site testing by Clarity technicians.";
  }
  if (/\b(program|configuration|integration)\b/i.test(haystack)) {
    return "System programming and integration so lighting, audio, and control work together reliably.";
  }
  if (/\b(design|engineering|documentation)\b/i.test(haystack)) {
    return "Design and documentation to ensure the installed system matches the agreed scope.";
  }
  if (/\b(project management|coordination)\b/i.test(haystack)) {
    return "Project coordination across trades so installation stays on schedule and on brief.";
  }

  const visible = clean(resolveStagingLineVisibleDescription(line));
  if (visible && visible.toLowerCase() !== serviceTitle.toLowerCase() && !isCatalogueLikeDescription(visible)) {
    return firstSentence(visible);
  }

  return "Professional delivery by Clarity to ensure the system performs as designed.";
}

/** Compact table columns — item and description must not repeat the same text. */
export function resolvePdfLineTableColumns(line: DtoolsStagingLine): PdfLineTableColumns {
  const brandModel = formatProductBrandModelReference({
    brand: line.manufacturer,
    model: line.model,
  });
  const displayName = resolvePdfProductDisplayName(line);
  const visible = clean(resolveStagingLineVisibleDescription(line));

  let item = brandModel || displayName || visible || clean(line.dtoolsDescription) || "Item";
  let description: string | null = null;

  if (line.bucket === "service") {
    const serviceTitle =
      (displayName && !isTechnicalModelIdentifier(displayName) ? displayName : null) ||
      clean(line.polishedDescription)?.split(/[.!?]/)[0]?.trim() ||
      clean(line.dtoolsDescription)?.split(/[.!?]/)[0]?.trim() ||
      item;
    item = serviceTitle;
    description = resolvePdfServiceBenefit(line, serviceTitle);
    if (description.toLowerCase() === serviceTitle.toLowerCase()) {
      description = null;
    }
    return { item, description, quantity: formatPdfQuantity(line) };
  }

  if (displayName) {
    item = displayName;
    description = resolvePdfProductExperienceSummary(line, displayName);
    const modelRef = clean(line.model);
    if (modelRef && modelRef !== displayName && !description?.includes(modelRef)) {
      const brand = clean(line.manufacturer);
      const technical = brand ? `${brand} ${modelRef}` : modelRef;
      description = description ? `${description} (${technical})` : technical;
    }
  } else if (brandModel) {
    item = brandModel;
    description = resolvePdfProductExperienceSummary(line, brandModel);
  } else {
    item = visible || clean(line.dtoolsDescription) || "Item";
    description = null;
  }

  if (description && description.toLowerCase() === item.toLowerCase()) {
    description = null;
  }

  return { item, description, quantity: formatPdfQuantity(line) };
}

function humanizePdfHighlight(text: string): string {
  const trimmed = clean(text);
  if (!trimmed) return trimmed;

  if (/\b\d+\s*-?\s*zone\s+amp/i.test(trimmed)) {
    return "Independent music control in multiple areas of the home";
  }
  if (/\b\d+\s*-?\s*zone\b/i.test(trimmed) && /\b(amp|audio|music)\b/i.test(trimmed)) {
    return "Independent music control in multiple areas of the home";
  }
  if (/\b\d+\s*watt\b/i.test(trimmed) || /\b\d+\s*channels?\b/i.test(trimmed)) {
    return "Clean, controlled power for realistic listening levels";
  }
  if (/^\d+\.\d+\.\d+$/i.test(trimmed)) {
    return "Immersive surround sound throughout the room";
  }

  return trimmed;
}

/** Split merged highlight blobs and prefer benefit phrasing over bare specifications. */
export function normalizePdfHighlights(highlights: string[]): string[] {
  const out: string[] = [];
  for (const raw of highlights) {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) continue;

    if (trimmed.includes("\n")) {
      out.push(
        ...trimmed
          .split("\n")
          .map((line) => line.replace(/^[\s*•✓\-–—]+/, "").trim())
          .filter(Boolean),
      );
      continue;
    }

    const camelSplit = trimmed.split(/(?<=[a-z])(?=[A-Z][a-z])/).map((s) => s.trim()).filter(Boolean);
    if (camelSplit.length > 1 && camelSplit.every((part) => part.length >= 4 && part.length <= 60)) {
      out.push(...camelSplit);
      continue;
    }

    out.push(trimmed);
  }

  const deduped: string[] = [];
  for (const h of out) {
    const polished = humanizePdfHighlight(h);
    if (!deduped.some((existing) => existing.toLowerCase() === polished.toLowerCase())) {
      deduped.push(polished);
    }
  }
  return deduped.slice(0, 7);
}
